import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { checkIsArchitect, checkIsBuildingInspector, checkIsClient } from "../../Utils/LoginUserRoleName";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent, DropdownProps } from "primereact/dropdown";
import { confirmDialog } from "primereact/confirmdialog";
import { Checkbox } from "primereact/checkbox";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Badge } from "primereact/badge";

import TabFilter from "../../Components/TabFilter";
import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import GoBackButton from "../../Components/GoBackButton";
import DataListModalSearch from "../../Components/DataListModal/DataListModalSearch";
import DataListModalClose from "../../Components/DataListModal/DataListModalClose";
import CustomTableLoader from "../../Components/CustomTableLoader";
import DataException from "../../Components/DataException";
import Textarea from "../../Components/TextArea";
import TextInput from "../../Components/TextInput";
import TableButton from "../../Components/TableButton";
import ViewDataGroup from "../../Components/DataViewModal/ViewDataGroup";
import DocumentViewModal from "../../Components/DocumentViewModal";
import NumberInput from "../../Components/NumberInput";
import FileActionLoader from "../../Components/FileActionLoader";
import ImagePreview from "../../Components/ImagePreview";
import { goToTop } from "../../Components/goToTop";
import { checkModelSelected, checkText } from "../../Utils/Validator";
import { documentFileExtension, imageFileExtension } from "../../Api/FileConstant";

import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import {
    BuildingInspectionCompany,
    BuildingControlQuotation as BuildingControlQuotationType,
    BuildingControlQuotationRequest,
    BuildingControlQuotationShareList,
    BuildingControlQuotationResponseField,
    BuildingControlQuotationResponseFile,
    BuildingControlQuotationComparison
} from "../../Controllers/Types/BuildingControl/BuildingControlQuotationType";

import { fetchAllProjects } from "../../Controllers/Thunks/projectThunks";
import {
    fetchBuildingInspectionCompanies, fetchBuildingControlQuotations, fetchBuildingControlQuotationRequests
} from "../../Controllers/Thunks/BuildingControlThunks";
import { approveBuildingControlQuotationAPI, compareBuildingControlQuotationAPI, createBuildingControlQuotationAPI, createQuotationARCResponseAPI, createQuotationBICResponseAPI, deleteBuildingControlQuotationResponseFile } from "../../Api/BuildingControlService/BuildingControlService";

const quotationStatusTabs = ['All', 'Pending', 'Finished'] as const;
type QuotationStatusTab = typeof quotationStatusTabs[number];

const BuildingControlQuotation: React.FC = () => {
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);

    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<QuotationStatusTab>('All');
    const [showBuildingInspectionCompanyModal, setShowBuildingInspectionCompanyModal] = useState<boolean>(false);
    const [showBuildingControlQuotationModal, setShowBuildingControlQuotationModal] = useState<boolean>(false);
    const [buildingInspectionCompanySearchKey, setBuildingInspectionCompanySearchKey] = useState<string>('');

    // State data
    const { allProjects: projects, loading: loadingProjects } = useAppSelector((state) => state.project);
    const {
        loadingBuildingInspectionCompanies, errorBuildingInspectionCompanies, buildingInspectionCompanies,
        loadingBuildingControlQuotations, errorBuildingControlQuotations, buildingControlQuotations,
        loadingBuildingControlQuotationRequests, errorBuildingControlQuotationRequests, buildingControlQuotationRequests,
    } = useAppSelector((state) => state.buildingControl);

    // Form fields
    const [project, setProject] = useState<Project | null>(null);
    const [projectName, setProjectName] = useState<string>('');
    const [projectAddress, setProjectAddress] = useState<string>('');
    const [selectedBuildingInspectionCompanies, setSelectedBuildingInspectionCompanies] = useState<BuildingInspectionCompany[]>([]);

    // Form errors
    const [projectError, setProjectError] = useState<string>('');
    const [projectNameError, setProjectNameError] = useState<string>('');
    const [projectAddressError, setProjectAddressError] = useState<string>('');
    const [selectedBuildingInspectionCompaniesError, setSelectedBuildingInspectionCompaniesError] = useState<string>('');

    const [selectedBuildingControlQuotation, setSelectedBuildingControlQuotation] = useState<BuildingControlQuotationType | null>(null);
    const [selectedBuildingControlQuotationRequest, setSelectedBuildingControlQuotationRequest] = useState<BuildingControlQuotationRequest | null>(null);
    const [selectedBuildingControlQuotationCompany, setSelectedBuildingControlQuotationCompany] = useState<BuildingControlQuotationShareList | null>(null);
    const [showResponseQuotationModal, setShowResponseQuotationModal] = useState<boolean>(false);

    // Response fields
    const [responseFileDeleteLoadingId, setResponseFileDeleteLoadingId] = useState<number | null>(null);
    const [responseFiles, setResponseFiles] = useState<File[]>([]);
    const [isResponseHasChanges, setIsResponseHasChanges] = useState<boolean>(false);

    // Keyed by BuildingControlQuotationResponseField.id
    const [responseFieldValues, setResponseFieldValues] = useState<Map<number, string | number | null>>(new Map());
    const [responseFieldErrors, setResponseFieldErrors] = useState<Map<number, string>>(new Map());

    // Response errors
    const [responseFilesError, setResponseFilesError] = useState<{ [key: string]: string }>({});

    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showItemDocViewModal, setShowItemDocViewModal] = useState<boolean>(false);

    // Comparison fields
    const [showCompareCompaniesModal, setShowCompareCompaniesModal] = useState<boolean>(false);
    const [showCompareQuotationModal, setShowCompareQuotationModal] = useState<boolean>(false);
    const [selectedComparisonCompanies, setSelectedComparisonCompanies] = useState<BuildingControlQuotationShareList[]>([]);
    const [quotationComparisonData, setQuotationComparisonData] = useState<BuildingControlQuotationComparison[]>([]);

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);
    const [isClient, setIsClient] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult, clientResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
                checkIsClient(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
            setIsClient(clientResult);
        };

        checkRole();
    }, []);
    /*  */

    /* Permissions */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddBuildingControlQuotation = useCanEdit("Building Control Quotation", "add_building_control_quotation");
    const hasPermissionForResponseBuildingControlQuotation = useCanEdit("Building Control Quotation", "response_building_control_quotation");
    const hasPermissionForCompareBuildingControlQuotation = useCanEdit("Building Control Quotation", "compare_building_control_quotation");
    /*  */

    const filteredBuildingControlQuotations = useMemo(() => {
        let filteredData = buildingControlQuotations;

        if (activeTab === 'All') return filteredData;
        if (activeTab === 'Pending') return filteredData.filter((e) => e.status === 'Pending');
        if (activeTab === 'Finished') return filteredData.filter((e) => e.status === 'Finished');

        return filteredData;
    }, [buildingControlQuotations, activeTab]);

    const filteredBuildingControlQuotationRequests = useMemo(() => {
        let filteredData = buildingControlQuotationRequests;

        if (activeTab === 'All') return filteredData;
        if (activeTab === 'Pending') return filteredData.filter((e) => e.status === 'Pending');
        if (activeTab === 'Finished') return filteredData.filter((e) => e.status === 'Finished');

        return filteredData;
    }, [buildingControlQuotationRequests, activeTab]);

    const filteredBuildingInspectionCompanies = useMemo(() => {
        const companies = buildingInspectionCompanies ?? [];
        const searchKey = buildingInspectionCompanySearchKey.trim().toLowerCase();

        if (!searchKey) {
            return companies;
        }

        return companies.filter((company) => {
            const companyName = company.company_name?.toLowerCase() ?? "";
            const email = company.email?.toLowerCase() ?? "";

            return companyName.includes(searchKey) || email.includes(searchKey);
        });
    }, [buildingInspectionCompanies, buildingInspectionCompanySearchKey]);

    const projectOptions = projects?.map((project) => ({
        ...project,
        disabled: buildingControlQuotations?.some(
            (quotation) => quotation?.project_id === project.project_id
        ),
    }));

    const handleGetBuildingControlQuotations = useCallback(async () => {
        let role: 'architect' | 'client' = 'architect';

        if (isArchitect) {
            role = 'architect';
        } else if (isClient) {
            role = 'client';
        }

        try {
            await dispatch(fetchBuildingControlQuotations(role)).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch building control quotations.",
                life: 3000,
            });
        } finally {
            setInitialDataLoading(false);
        }
    }, [dispatch, isArchitect, isClient]);

    const handleGetBuildingControlQuotationRequests = useCallback(async () => {
        try {
            await dispatch(fetchBuildingControlQuotationRequests()).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch building control quotation requests.",
                life: 3000,
            });
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAllProjects());
        dispatch(fetchBuildingInspectionCompanies()).unwrap();

        if (isArchitect || isClient) {
            handleGetBuildingControlQuotations();
        }

        if (isInspector) {
            handleGetBuildingControlQuotationRequests();
        }
    }, [
        dispatch,
        handleGetBuildingControlQuotations,
        handleGetBuildingControlQuotationRequests,
        isArchitect,
        isInspector,
        isClient,
    ]);

    /* **************************************************************** Building inspection company */
    const handleShowBuildingInspectionCompanyModal = () => {
        setShowBuildingInspectionCompanyModal(true);
    };

    const handleCloseBuildingInspectionCompanyModal = (isClear: boolean) => {
        if (isClear) {
            setSelectedBuildingInspectionCompanies([]);
        }

        setBuildingInspectionCompanySearchKey('');
        setShowBuildingInspectionCompanyModal(false);
    };

    const handleSelectBuildingInspectionCompany = (company: BuildingInspectionCompany, isChecked: boolean) => {
        if (isChecked) {
            setSelectedBuildingInspectionCompanies((prevState) => [...prevState, company]);
        } else {
            setSelectedBuildingInspectionCompanies((prevState) =>
                prevState.filter(
                    (existingCompany) => existingCompany.id !== company.id
                )
            );
        }
    };

    const isSelectedBuildingInspectionCompany = (company: BuildingInspectionCompany): boolean =>
        selectedBuildingInspectionCompanies.some(
            (existingCompany) => existingCompany?.id === company?.id
        );

    const buildingInspectionCompanyModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={() => handleCloseBuildingInspectionCompanyModal(true)}
            />

            <Button
                label={`Add${selectedBuildingInspectionCompanies?.length > 0 ? `(${String(selectedBuildingInspectionCompanies?.length).padStart(2, '0')})` : ''}`}
                onClick={() => handleCloseBuildingInspectionCompanyModal(false)}
                className="custom_btn primary"
                disabled={selectedBuildingInspectionCompanies?.length === 0}
            />
        </div>
    );
    /* **************************************************************** */


    /* **************************************************************** Building control quotation */
    const handleFilterBuildingControlQuotation = (filterState: QuotationStatusTab) => {
        setActiveTab(filterState);
        goToTop();
    };

    const selectedProjectTemplate = (option: Project, props: DropdownProps) => {
        if (option) {
            return (
                <span>
                    <b>{option?.job_number}</b>&nbsp;-&nbsp;{option?.address}
                </span>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const projectOptionTemplate = (option: Project & { disabled?: boolean }) => {
        return (
            <div
                className={`address_option_container d-flex justify-content-between align-items-center 
                    ${option?.project_type === "Commercial" ? "bg-blue" : "bg-purple"}
                    ${option?.disabled ? "disabled_option" : ""}
                `}
            >
                <span>
                    <b>{option?.job_number}</b>:&nbsp;
                    {option?.address}

                    {option?.disabled && (
                        <small className="ms-2 text-danger">
                            (Already quoted)
                        </small>
                    )}
                </span>

                <Tooltip
                    target={`.project_${option?.project_id}_type`}
                    position="left"
                />
                <span className={`project_type_badge project_${option?.project_id}_type`} data-pr-tooltip={option?.project_type === 'Commercial' ? 'Commercial Project' : 'Domestic Project'}>
                    {option?.project_type === 'Commercial' ? 'CP' : 'DP'}
                </span>
            </div>
        );
    };

    const handleCloseBuildingControlQuotationModal = () => {
        setShowBuildingControlQuotationModal(false);
        handleResetBuildingControlQuotationFields();
    };

    // Clear errors
    useEffect(() => setProjectError(""), [project]);
    useEffect(() => setProjectNameError(""), [projectName]);
    useEffect(() => setProjectAddressError(""), [projectAddress]);
    useEffect(() => setSelectedBuildingInspectionCompaniesError(""), [selectedBuildingInspectionCompanies]);

    // Check vaidation
    function checkCredentialsForBuildingControlQuotation(): boolean {
        const isValidProject = checkModelSelected(project !== null, true, setProjectError);
        const isValidProjectName = checkText(projectName ?? '', true, setProjectNameError);
        const isValidProjectAddress = checkText(projectAddress ?? '', true, setProjectAddressError);

        const isValidBuildingInspectionCompanies =
            (selectedBuildingInspectionCompanies?.length ?? 0) > 0;

        setSelectedBuildingInspectionCompaniesError(
            isValidBuildingInspectionCompanies
                ? ''
                : 'Select at least one building inspection company'
        );

        return (
            isValidProject &&
            isValidProjectName &&
            isValidProjectAddress &&
            isValidBuildingInspectionCompanies
        );
    }

    // Check API Error
    function showAPIErrorForBuildingControlQuotation(keyName: string, error: string): void {
        switch (keyName) {
            case "project_id":
                setProjectError(error);
                break;
            case "project_name":
                setProjectNameError(error);
                break;
            case "project_address":
                setProjectAddressError(error);
                break;
            case "share_list[]":
                setSelectedBuildingInspectionCompaniesError(error);
                break;
            default:
                break;
        }
    }

    const handleResetBuildingControlQuotationFields = () => {
        setProject(null);
        setProjectName('');
        setProjectAddress('');
        setSelectedBuildingInspectionCompanies([]);

        setProjectError('');
        setProjectNameError('');
        setProjectAddressError('');
        setSelectedBuildingInspectionCompaniesError('');
    };

    // --------------------------------------- Create building control quotation
    const handleAddBuildingControlQuotation = () => {
        setShowBuildingControlQuotationModal(true);
    };

    const handleCreateBuildingControlQuotation = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForBuildingControlQuotation()) return;

        setLoading(true);

        try {
            const project_id = project?.project_id ?? 0;
            const project_name = projectName ?? '';
            const project_address = projectAddress ?? '';
            const share_list = selectedBuildingInspectionCompanies?.map((e) => e.id);

            const response = await createBuildingControlQuotationAPI(
                project_id,
                project_name,
                project_address,
                share_list ?? [],
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotations();
                handleCloseBuildingControlQuotationModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Building control quotation created successfully.",
                    life: 3000,
                });
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) => (
                    showAPIErrorForBuildingControlQuotation(key, value[0])
                ));
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error creating the building control quotation.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error adding the building control quotation:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the building control quotation.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------

    const buildingControlQuotationModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Add Building Control Quotation</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseBuildingControlQuotationModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const buildingControlQuotationModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary d-none d-sm-flex"
                onClick={handleCloseBuildingControlQuotationModal}
            />

            <Button
                label={loading ? "Processing" : "Submit"}
                onClick={handleCreateBuildingControlQuotation}
                className="custom_btn primary"
                loading={loading}
            />
        </div>
    );

    const quotationShareStatusBody = (rowData: BuildingControlQuotationShareList, quotation: BuildingControlQuotationType) => {
        const isApproved = quotation?.status === 'Finished' && rowData?.is_approved === 1;
        const isRejected = quotation?.status === 'Finished' && rowData?.is_approved === 0;
        const isPending = quotation?.status === 'Pending' && rowData?.respone_data !== null;
        const notResponded = quotation?.status === 'Pending' && rowData?.respone_data === null;

        let color = "";
        let bgColor = "";
        let status = "";

        if (isApproved) {
            color = "#06b500";
            bgColor = "#d5f8df";
            status = "Approved";
        } else if (isRejected) {
            color = "#ff0000";
            bgColor = "#ffd6d6";
            status = "Not Approved";
        } else if (isPending) {
            color = "#3b82f6";
            bgColor = "#e0e7ff";
            status = "Pending";
        } else if (notResponded) {
            color = "#de9725";
            bgColor = "#f8f0d6";
            status = "Not Responded";
        } else {
            color = "#656565";
            bgColor = "#EEE";
            status = "---";
        }

        return (
            <div className="flex_center">
                <span className="priority_label xl" style={{ color: color, backgroundColor: bgColor }}>
                    {status}
                </span>
            </div>
        );
    };

    const quotationTableARCResponseBody = (rowData: BuildingControlQuotationShareList, quotation: BuildingControlQuotationType) => {
        const isResponsed = rowData?.respone_data !== null;
        const isBICResponsed = rowData?.respone_data?.responce_added_arc === 0;
        const isApproved = quotation?.status === 'Finished';

        return (
            <div className="tabel_btn_area justify-content-center mt-1 mb-1">
                {isClient ? (
                    <Button
                        label="View"
                        icon="bi bi-eye"
                        className="table_status_btn fixed_width"
                        onClick={() => handleResponseQuotationByARC(quotation, rowData)}
                        tooltipOptions={{ position: "left" }}
                        tooltip="Click to view response"
                    />
                ) : (
                    <Button
                        severity={isApproved ? "secondary" : isBICResponsed ? "secondary" : isResponsed ? "success" : "warning"}
                        icon={`bi bi-${isApproved ? "eye" : isBICResponsed ? "eye" : isResponsed ? "pencil-square" : "reply"}`}
                        label={isApproved ? "View" : isBICResponsed ? "View" : isResponsed ? "Edit" : "Response"}
                        className="table_status_btn fixed_width"
                        onClick={() => {
                            if (hasPermissionForResponseBuildingControlQuotation) {
                                handleResponseQuotationByARC(quotation, rowData);
                            }
                        }}
                        tooltipOptions={{ position: "left" }}
                        tooltip={isApproved ? "Click to view response" : isBICResponsed ? "Click to view response" : isResponsed ? "Click to edit response" : "Click to add response"}
                        disabled={!hasPermissionForResponseBuildingControlQuotation}
                    />
                )}
            </div>
        )
    }

    const getRowClassNameForQuotationShare = (data: BuildingControlQuotationShareList, quotation: BuildingControlQuotationType) => {
        const isApproved = quotation?.status === 'Finished' && data?.is_approved === 1;
        const isRejected = quotation?.status === 'Finished' && data?.is_approved === 0;

        if (isApproved) {
            return "project_data_row success";
        } else if (isRejected) {
            return "project_data_row danger";
        } else {
            return "project_data_row secondary";
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Building control quotation request */
    const getRowClassNameForQuotationRequest = (data: BuildingControlQuotationRequest) => {
        const isApproved = data?.status === 'Finished' && data?.is_approved === 1;
        const isRejected = data?.status === 'Finished' && data?.is_approved === 0;

        if (isApproved) {
            return "project_data_row success";
        } else if (isRejected) {
            return "project_data_row danger";
        } else {
            return "project_data_row secondary";
        }
    }

    const bicQuotationStatusBody = (rowData: BuildingControlQuotationRequest) => {
        const isApproved = rowData?.status === 'Finished' && rowData?.is_approved === 1;
        const isRejected = rowData?.status === 'Finished' && rowData?.is_approved === 0;
        const isPending = rowData?.status === 'Pending' && rowData?.responce_data !== null;
        const notResponded = rowData?.status === 'Pending' && rowData?.responce_data === null;

        let color = "";
        let bgColor = "";
        let status = "";

        if (isApproved) {
            color = "#06b500";
            bgColor = "#d5f8df";
            status = "Approved";
        } else if (isRejected) {
            color = "#ff0000";
            bgColor = "#ffd6d6";
            status = "Rejected";
        } else if (isPending) {
            color = "#3b82f6";
            bgColor = "#e0e7ff";
            status = "Pending";
        } else if (notResponded) {
            color = "#de9725";
            bgColor = "#f8f0d6";
            status = "Not Responded";
        } else {
            color = "#656565";
            bgColor = "#EEE";
            status = "---";
        }

        return (
            <div className="flex_center">
                <span className="priority_label xl" style={{ color: color, backgroundColor: bgColor }}>
                    {status}
                </span>
            </div>
        );
    };

    const quotationTableBICResponseBody = (rowData: BuildingControlQuotationRequest) => {
        const isResponsed = rowData?.responce_data !== null;
        const isARCResponsed = rowData?.responce_data?.responce_added_arc === 1;
        const isApproved = rowData?.status === 'Finished';

        return (
            <div className="tabel_btn_area justify-content-center mt-1 mb-1">
                <Button
                    severity={isApproved ? "secondary" : isARCResponsed ? "secondary" : isResponsed ? "success" : "warning"}
                    label={isApproved ? "View" : isARCResponsed ? "View" : isResponsed ? "Edit" : "Response"}
                    icon={`bi bi-${isApproved ? "eye" : isARCResponsed ? "eye" : isResponsed ? "pencil-square" : "reply"}`}
                    className="table_status_btn fixed_width"
                    onClick={() => {
                        if (hasPermissionForResponseBuildingControlQuotation) {
                            handleResponseQuotationByBIC(rowData);
                        }
                    }}
                    tooltipOptions={{ position: "left" }}
                    tooltip={isApproved ? "Click to view response" : isARCResponsed ? "Click to view response" : isResponsed ? "Click to edit response" : "Click to add response"}
                    disabled={!hasPermissionForResponseBuildingControlQuotation}
                />
            </div>
        )
    }
    /* **************************************************************** */


    /* **************************************************************** Approve quotation */
    const handleApproveQuotation = (companyId: number | null) => {
        if (!selectedBuildingControlQuotation) return;

        confirmDialog({
            message: "Are you sure you want to approve this company's quotation?",
            header: "Confirm the approval",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => approveQuotation(companyId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const approveQuotation = async (companyId: number | null) => {
        if (!selectedBuildingControlQuotation) return;

        if (!companyId) {
            toast?.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "No company selected to approve.",
                life: 3000,
            });
            return;
        }

        try {
            const bc_quotation_id = selectedBuildingControlQuotation?.id;
            const company_id = companyId;

            const response = await approveBuildingControlQuotationAPI(
                bc_quotation_id,
                company_id,
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotations();
                handleCloseCompareQuotationModal();
                handleCloseCompareCompaniesModal();
                handleCloseResponseQuotationModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Quotation has been approved successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error approving the quotation.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error approving the quotation:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while approving the quotation.",
                life: 3000,
            });
        }
    }
    /* **************************************************************** */


    /* **************************************************************** Response Quotation */
    const handleCloseResponseQuotationModal = () => {
        setShowResponseQuotationModal(false);
        handleResetResponseQuotationFields();
    };

    const handleResetResponseQuotationFields = () => {
        setSelectedBuildingControlQuotation(null);
        setSelectedBuildingControlQuotationCompany(null);
        setSelectedBuildingControlQuotationRequest(null);

        setResponseFiles([]);
        setResponseFilesError({});
        setResponseFieldValues(new Map());
        setResponseFieldErrors(new Map());
    };

    // Clear errors
    useEffect(() => setResponseFilesError({}), [responseFiles]);

    const initResponseFieldValues = (fields: BuildingControlQuotationResponseField[]) => {
        const map = new Map<number, string | number | null>();
        fields.forEach((field) => {
            map.set(
                field.id,
                field.responce?.reply !== undefined && field.responce?.reply !== null
                    ? field.responce.reply
                    : field.answer_type === 'number' ? null : ''
            );
        });
        setResponseFieldValues(map);
        setResponseFieldErrors(new Map());
    };

    const getResponseFields = (): BuildingControlQuotationResponseField[] => {
        if (isArchitect || isClient) {
            return [...(selectedBuildingControlQuotationCompany?.responce_data_list ?? [])]
                .sort((a, b) => Number(a.order_number ?? 0) - Number(b.order_number ?? 0));
        }
        if (isInspector) {
            return [...(selectedBuildingControlQuotationRequest?.responce_data_list ?? [])]
                .sort((a, b) => Number(a.order_number ?? 0) - Number(b.order_number ?? 0));
        }
        return [];
    };

    const getResponseFiles = (): BuildingControlQuotationResponseFile[] => {
        if (isArchitect || isClient) {
            return selectedBuildingControlQuotationCompany?.file_list ?? [];
        }
        if (isInspector) {
            return selectedBuildingControlQuotationRequest?.file_list ?? [];
        }
        return [];
    };

    // Response field on change handle.
    const handleResponseFieldChange = (fieldId: number, value: string | number | null) => {
        setResponseFieldValues((prev) => {
            const next = new Map(prev);
            next.set(fieldId, value);
            return next;
        });

        setResponseFieldErrors((prev) => {
            if (!prev.has(fieldId)) return prev;
            const next = new Map(prev);
            next.delete(fieldId);
            return next;
        });
    };

    // Validates fields
    const validateResponseFields = (): boolean => {
        const fields = getResponseFields();
        const errors = new Map<number, string>();

        fields.forEach((field) => {
            if (field.is_requireded !== 1) return;
            const value = responseFieldValues.get(field.id);
            const isEmpty =
                value === null ||
                value === undefined ||
                (typeof value === 'string' && value.trim() === '') ||
                (typeof value === 'number' && isNaN(value));

            if (isEmpty) {
                errors.set(field.id, `${field.title} is required.`);
            }
        });

        setResponseFieldErrors(errors);
        return errors.size === 0;
    };

    // Check API Error
    function showAPIErrorForResponseQuotation(keyName: string, error: string): void {
        const fields = getResponseFields();

        const perFieldMatch = keyName.match(/^responce_list\.([0-9]+)\.reply$/);

        if (perFieldMatch) {
            const index = parseInt(perFieldMatch[1], 10);
            const field = fields[index];
            if (field) {
                setResponseFieldErrors((prev) => {
                    const next = new Map(prev);
                    next.set(field.id, error);
                    return next;
                });
            }
            return;
        }

        const fileListIndexMatch = keyName.match(/^file_list\.([0-9]+)$/);
        if (fileListIndexMatch) {
            setResponseFilesError((prev) => ({ ...prev, [keyName]: error }));
            return;
        }

        if (keyName === 'file_list' || keyName === 'file_list[]') {
            setResponseFilesError((prev) => ({ ...prev, [keyName]: error }));
            return;
        }
    }

    // --------------------------------------- Response quotation for ARC
    const handleResponseQuotationByARC = (quotation: BuildingControlQuotationType | null, company: BuildingControlQuotationShareList | null) => {
        if (!quotation || !company) return;
        setSelectedBuildingControlQuotation(quotation);
        setSelectedBuildingControlQuotationCompany(company);

        // Seed values from existing replies (edit) or empty (add)
        const fields = [...(company.responce_data_list ?? [])]
            .sort((a, b) => Number(a.order_number ?? 0) - Number(b.order_number ?? 0));
        initResponseFieldValues(fields);

        setShowResponseQuotationModal(true);
    };

    const handleCreateBuildingControlQuotationARCResponse = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedBuildingControlQuotation || !selectedBuildingControlQuotationCompany) return;
        if (!validateResponseFields()) return;

        setLoading(true);

        try {
            const bc_quotation_id = selectedBuildingControlQuotation.id;
            const company_id = selectedBuildingControlQuotationCompany.company_id;

            // Build responce_list payload from the current field values map
            const fields = getResponseFields();
            const responce_list = fields.map((field) => ({
                responce_list_id: field.id,
                reply: responseFieldValues.get(field.id) ?? null,
            }));

            const response = await createQuotationARCResponseAPI(
                bc_quotation_id,
                company_id,
                responce_list,
                responseFiles
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotations();
                handleCloseResponseQuotationModal();

                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Response added successfully.",
                    life: 3000,
                });
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? {}).forEach(([key, value]) =>
                    showAPIErrorForResponseQuotation(key, (value as string[])[0])
                );
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error adding the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error adding ARC response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------


    // --------------------------------------- Response quotation for BIC
    const handleResponseQuotationByBIC = (quotationRequest: BuildingControlQuotationRequest | null) => {
        if (!quotationRequest) return;
        setSelectedBuildingControlQuotationRequest(quotationRequest);

        const fields = [...(quotationRequest.responce_data_list ?? [])]
            .sort((a, b) => Number(a.order_number ?? 0) - Number(b.order_number ?? 0));
        initResponseFieldValues(fields);

        setShowResponseQuotationModal(true);
    };

    const handleCreateBuildingControlQuotationBICResponse = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedBuildingControlQuotationRequest) return;
        if (!validateResponseFields()) return;

        setLoading(true);

        try {
            const bc_quotation_id = selectedBuildingControlQuotationRequest.id;

            const fields = getResponseFields();

            const responce_list = fields.map((field) => ({
                responce_list_id: field.id,
                reply: responseFieldValues.get(field.id) ?? null,
            }));

            const response = await createQuotationBICResponseAPI(
                bc_quotation_id,
                responce_list,
                responseFiles
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlQuotationRequests();
                handleCloseResponseQuotationModal();

                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Response added successfully.",
                    life: 3000,
                });
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? {}).forEach(([key, value]) =>
                    showAPIErrorForResponseQuotation(key, (value as string[])[0])
                );
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error adding the response.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error adding BIC response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding the response.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    };
    // ---------------------------------------


    // --------------------------------------- Response files
    const handleDeleteResponseFile = (fileId: number) => {
        confirmDialog({
            message: "Are you sure you want to delete this file?",
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteResponseFile(fileId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    };

    const deleteResponseFile = async (fileId: number) => {
        if (!fileId) return;

        setResponseFileDeleteLoadingId(fileId);

        try {
            const result = await deleteBuildingControlQuotationResponseFile(fileId);

            if (result.statusCode === 200) {
                if (isArchitect) {
                    setSelectedBuildingControlQuotationCompany((prev) =>
                        prev ? { ...prev, file_list: prev.file_list.filter((f) => f.id !== fileId) } : prev
                    );
                    handleGetBuildingControlQuotations();
                } else if (isInspector) {
                    setSelectedBuildingControlQuotationRequest((prev) =>
                        prev ? { ...prev, file_list: prev.file_list.filter((f) => f.id !== fileId) } : prev
                    );
                    handleGetBuildingControlQuotationRequests();
                }

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "File deleted successfully.",
                    life: 2000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the file.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error deleting the file:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the file.",
                life: 3000,
            });
        } finally {
            setResponseFileDeleteLoadingId(null);
        }
    };

    const handleUploadResponseFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setResponseFiles((prev) => [...prev, ...filesArray]);
        }
    };

    const handleRemoveResponseFile = (fileIndex: number) => {
        setResponseFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    };
    // ---------------------------------------


    // --------------------------------------- View file
    const handleCloseFileViewModal = () => {
        setShowItemDocViewModal(false);
    };

    const handleViewDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowItemDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };
    // ---------------------------------------

    const responseQuotationModalHeader = () => {
        const isResponsed = (isArchitect && selectedBuildingControlQuotationCompany?.respone_data !== null) || (isInspector && selectedBuildingControlQuotationRequest?.responce_data !== null);
        const isARCResponsed = selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 1;
        const isBICResponsed = selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 0;
        const isApproved = (isArchitect && selectedBuildingControlQuotation?.status === 'Finished') || (isInspector && selectedBuildingControlQuotationRequest?.status === 'Finished');

        return (
            <div className="custom_modal_header_inner">
                <h5 className="modal-title fs-5">{isApproved ? "View" : (isARCResponsed || isBICResponsed || isClient) ? 'View' : isResponsed ? 'Edit' : 'Add'} Response</h5>
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseResponseQuotationModal}
                >
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            </div>
        )
    };

    const responseQuotationModalFooter = () => {
        const isResponsed = (isArchitect && selectedBuildingControlQuotationCompany?.respone_data !== null) || (isInspector && selectedBuildingControlQuotationRequest?.responce_data !== null);
        const isARCResponsed = selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 1;
        const isBICResponsed = selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 0;
        const handleSubmitResponse = isArchitect ? handleCreateBuildingControlQuotationARCResponse : isInspector ? handleCreateBuildingControlQuotationBICResponse : () => { };
        const isApproved = (isArchitect && selectedBuildingControlQuotation?.status === 'Finished') || (isInspector && selectedBuildingControlQuotationRequest?.status === 'Finished');
        const notResponded = selectedBuildingControlQuotationCompany?.respone_data === null;

        return (
            <div className="custom_modal_footer">
                <Button
                    label="Close"
                    className="custom_btn secondary d-none d-sm-flex"
                    onClick={handleCloseResponseQuotationModal}
                />

                {!isApproved && ((isInspector && !isARCResponsed) || (isArchitect && !isBICResponsed)) && (
                    <Button
                        label={loading ? "Processing" : isResponsed ? "Update" : "Submit"}
                        onClick={handleSubmitResponse}
                        className="custom_btn primary"
                        loading={loading}
                    />
                )}

                {isClient && selectedBuildingControlQuotation?.status !== 'Finished' && (
                    <Button
                        label="Approve"
                        onClick={() => {
                            if (notResponded) return;
                            handleApproveQuotation(selectedBuildingControlQuotationCompany?.company_id ?? null);
                        }}
                        className="custom_btn primary"
                        disabled={notResponded}
                    />
                )}
            </div>
        )
    };
    /* **************************************************************** */


    /* **************************************************************** Compare quotation */
    const handleCompareQuotation = (quotation: BuildingControlQuotationType | null) => {
        if (!quotation) return;

        setSelectedBuildingControlQuotation(quotation);
        setShowCompareCompaniesModal(true);
    };

    const handleSubmitCompareQuotation = async () => {
        if (!selectedBuildingControlQuotation) return;

        setLoading(true);

        try {
            const bc_quotation_id = selectedBuildingControlQuotation?.id;
            const comparison = selectedComparisonCompanies?.map((e) => e.company_id);

            const response = await compareBuildingControlQuotationAPI(
                bc_quotation_id,
                comparison ?? [],
            );

            if (response.statusCode === 200) {
                setQuotationComparisonData(response?.data || []);
                setShowCompareCompaniesModal(false);
                setShowCompareQuotationModal(true);
            } else if (response.statusCode === 422) {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "Atleast select two companies for comparison.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error comparing quotation.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error('Error Comparing Building Control Quotation:', error);

            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while comparing building control quotation.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleCloseCompareCompaniesModal = () => {
        setSelectedBuildingControlQuotation(null);
        setQuotationComparisonData([]);
        setSelectedComparisonCompanies([]);
        setShowCompareCompaniesModal(false);
    };

    const compareCompaniesModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select Companies for Comparison
                {selectedComparisonCompanies && selectedComparisonCompanies?.length > 0 && (
                    <span className="modal_sub_title">
                        ({selectedComparisonCompanies?.length} {selectedComparisonCompanies?.length > 1 ? 'companies' : 'company'} selected)
                    </span>
                )}
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseCompareCompaniesModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const compareCompaniesModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseCompareCompaniesModal}
            />

            <Button
                label="Submit"
                onClick={handleSubmitCompareQuotation}
                className="custom_btn primary"
                loading={loading}
                disabled={selectedComparisonCompanies?.length < 2}
            />
        </div>
    );

    const handleSelectQuotationShareListCompany = (quotationShare: BuildingControlQuotationShareList, isChecked: boolean) => {
        if (isChecked) {
            setSelectedComparisonCompanies((prevState) => [...prevState, quotationShare]);
        } else {
            setSelectedComparisonCompanies((prevState) =>
                prevState.filter(
                    (existingCompany) => existingCompany.id !== quotationShare.id
                )
            );
        }
    };

    const isSelectedQuotationShareCompany = (quotationShare: BuildingControlQuotationShareList): boolean =>
        selectedComparisonCompanies.some(
            (existingCompany) => existingCompany?.id === quotationShare?.id
        );

    // Compare quotation
    const handleCloseCompareQuotationModal = () => {
        setQuotationComparisonData([]);
        setShowCompareCompaniesModal(true);
        setShowCompareQuotationModal(false);
    }

    const compareQuotationModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Quotation Comparison
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseCompareQuotationModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );
    /* **************************************************************** */


    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingBuildingControlQuotations && loadingBuildingControlQuotationRequests && initialDataLoading ? (
                    <PageLoader />
                ) : (errorBuildingControlQuotations || errorBuildingControlQuotationRequests) ? (
                    <ErrorPage errorMessage={errorBuildingControlQuotations ?? errorBuildingControlQuotationRequests ?? ''} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Building control quotation </h4>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {quotationStatusTabs?.map((tab) => {
                                    const isDisabled = (isArchitect && buildingControlQuotations?.length === 0) || (isInspector && buildingControlQuotationRequests?.length === 0);
                                    const tabSeverety = tab === 'All' ? 'primary' : tab === 'Pending' ? 'warning' : tab === 'Finished' ? 'success' : '';

                                    return (
                                        <TabFilter
                                            key={tab}
                                            label={tab}
                                            isActive={activeTab === tab}
                                            onTabChange={() => handleFilterBuildingControlQuotation(tab)}
                                            tabClassName={tabSeverety}
                                            disabled={isDisabled}
                                        />
                                    )
                                })}
                            </div>

                            <div className="page_action_area">
                                {isArchitect && hasPermissionForAddBuildingControlQuotation && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddBuildingControlQuotation}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add quotation</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        {(isArchitect || isClient) && (
                            <div className="page_main_content_section">
                                {loadingBuildingControlQuotations && (
                                    <CustomTableLoader />
                                )}

                                {filteredBuildingControlQuotations && filteredBuildingControlQuotations?.length > 0 ? (
                                    <React.Fragment>
                                        {filteredBuildingControlQuotations?.map((quotation) => {
                                            return (
                                                <div key={`bcq_${quotation?.id}`} className="page_sub_content_section">
                                                    <div className="page_sub_content_header">
                                                        <div className="page_sub_content_sub">
                                                            <h4 className="page_sub_content_head text-capitalize">
                                                                <i className="bi bi-buildings me-2"></i>
                                                                {quotation?.project_detail?.job_number}<span className="text_subtext"> : {quotation?.project_detail?.full_address}</span>
                                                            </h4>
                                                        </div>

                                                        <div className="page_sub_content_header_btn_area">
                                                            {(isClient && hasPermissionForCompareBuildingControlQuotation && quotation?.status !== 'Finished') && (
                                                                <TableButton
                                                                    label="Compare"
                                                                    icon="bi bi-arrow-left-right"
                                                                    iconClassName="me-2"
                                                                    disabled={quotation?.quotation_share_list?.length === 1}
                                                                    onClick={() => handleCompareQuotation(quotation)}
                                                                />
                                                            )}

                                                            {activeTab === 'All' && (
                                                                <span className={`sub_content_badge as_label ${quotation?.status === 'Pending' ? 'warning' : quotation?.status === 'Finished' ? 'success' : ''}`}>
                                                                    {quotation?.status}
                                                                </span>
                                                            )}

                                                            <span className="sub_content_badge">
                                                                {String(quotation?.quotation_share_list?.length ?? 0).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="page_sub_content_body">
                                                        {quotation?.quotation_share_list && quotation?.quotation_share_list?.length > 0 ? (
                                                            <DataTable
                                                                key={`quotationShareList_${quotation?.id}`}
                                                                value={quotation?.quotation_share_list}
                                                                size="small"
                                                                dataKey="id"
                                                                className="page_table p-0"
                                                                rowClassName={(data: BuildingControlQuotationShareList) => getRowClassNameForQuotationShare(data, quotation)}
                                                                rowHover
                                                                scrollable
                                                                tableStyle={{ minWidth: "50rem" }}
                                                                scrollHeight="490px"
                                                                loading={loadingBuildingControlQuotations}
                                                                emptyMessage="No companies found!"
                                                            >
                                                                <Column
                                                                    header="Company Name"
                                                                    headerClassName="text_purple"
                                                                    field="company.company_name"
                                                                    body={(rowData: BuildingControlQuotationShareList) => (
                                                                        <span className="text-capitalize">
                                                                            {rowData?.company?.company_name
                                                                                ? rowData?.company?.company_name
                                                                                : "---"}
                                                                        </span>
                                                                    )}
                                                                    style={{ width: "30%" }}
                                                                ></Column>

                                                                <Column
                                                                    header="Email"
                                                                    headerClassName="text_purple"
                                                                    field="company.email"
                                                                    body={(rowData: BuildingControlQuotationShareList) => (
                                                                        <span>
                                                                            {rowData?.company?.email
                                                                                ? rowData?.company?.email
                                                                                : "---"}
                                                                        </span>
                                                                    )}
                                                                    style={{ width: "30%" }}
                                                                ></Column>

                                                                <Column
                                                                    header="Status"
                                                                    alignHeader={"center"}
                                                                    headerClassName="text_purple"
                                                                    body={(rowData: BuildingControlQuotationShareList) => quotationShareStatusBody(rowData, quotation)}
                                                                    style={{ width: "15%" }}
                                                                ></Column>

                                                                <Column
                                                                    header="Response"
                                                                    alignHeader={'center'}
                                                                    headerClassName="text_purple"
                                                                    body={(rowData: BuildingControlQuotationShareList) => quotationTableARCResponseBody(rowData, quotation)}
                                                                    style={{ width: "25%" }}
                                                                ></Column>
                                                            </DataTable>
                                                        ) : (
                                                            <NoDataCreated
                                                                containerClassname="pos_rel sub"
                                                                message={`The ${quotation?.project_name} quotation has not been shared with any companies yet. ${hasPermissionForAddBuildingControlQuotation ? 'To share with companies, click the Add button' : ''}`}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </React.Fragment>
                                ) : (
                                    <NoDataCreated message={`No ${activeTab !== 'All' ? activeTab.toLowerCase() : ''} building control quotation found`} />
                                )}
                            </div>
                        )}

                        {isInspector && (
                            <div className="page_main_content_section">
                                <DataTable
                                    key={`quotationRequest`}
                                    value={filteredBuildingControlQuotationRequests}
                                    size="small"
                                    dataKey="id"
                                    className="page_table p-0"
                                    rowClassName={getRowClassNameForQuotationRequest}
                                    rowHover
                                    scrollable
                                    tableStyle={{ minWidth: "50rem" }}
                                    scrollHeight="490px"
                                    loading={loadingBuildingControlQuotations}
                                    emptyMessage="No building control quotation requests found!"
                                >
                                    <Column
                                        header="Project"
                                        headerClassName="text_purple"
                                        field="project_detail.full_address"
                                        body={(rowData: BuildingControlQuotationRequest) => (
                                            <span className="text-capitalize">
                                                <span className="text_bold">{rowData?.project_detail?.job_number} :{' '}</span>
                                                {rowData?.project_detail?.full_address
                                                    ? rowData?.project_detail?.full_address
                                                    : "---"}
                                            </span>
                                        )}
                                        style={{ width: "40%" }}
                                    ></Column>

                                    <Column
                                        header="Status"
                                        alignHeader={"center"}
                                        headerClassName="text_purple"
                                        body={bicQuotationStatusBody}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Response"
                                        alignHeader={'center'}
                                        headerClassName="text_purple"
                                        body={(rowData: BuildingControlQuotationRequest) => quotationTableBICResponseBody(rowData)}
                                        style={{ width: "30%" }}
                                    ></Column>
                                </DataTable>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Building control quotation modal */}
            <Dialog
                visible={showBuildingControlQuotationModal}
                headerClassName="custom_modal_header"
                header={buildingControlQuotationModalHeader}
                footer={buildingControlQuotationModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseBuildingControlQuotationModal}
                dismissableMask={false}
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        <div className="col-12">
                            <Message
                                severity='warn'
                                className="message_text w-100 mb-4"
                                icon="bi bi-exclamation-circle"
                                style={{ border: '1px solid', borderRadius: '8px' }}
                                text={`Please make sure all the details are correct before submitting the request beacuse you won't be able to edit the details after submitting the request.`}
                            />
                        </div>

                        {/* Project */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="Project" className="custom_form_label is_required">
                                    Project
                                </label>

                                <Dropdown
                                    id="project"
                                    value={project}
                                    onChange={(e: DropdownChangeEvent) => setProject(e.value)}
                                    options={projectOptions}
                                    optionLabel="job_number"
                                    optionDisabled="disabled"
                                    valueTemplate={selectedProjectTemplate}
                                    itemTemplate={projectOptionTemplate}
                                    emptyFilterMessage="No project(s) found!"
                                    placeholder="Select project"
                                    className="form_dropdown address_selection w-100"
                                    filter
                                    showClear
                                    filterInputAutoFocus
                                    filterBy="job_number,address"
                                    filterPlaceholder="Search by address/job no."
                                    panelClassName="address_dropdown_panel"
                                    loading={loadingProjects}
                                />

                                {projectError && (
                                    <small className="form_error_msg">{projectError}</small>
                                )}
                            </div>
                        </div>

                        {/* Project Name */}
                        <div className="col-12">
                            <TextInput
                                id="projectName"
                                label="Project Name"
                                labelHtmlFor="projectName"
                                required={true}
                                value={projectName}
                                placeholder="Project Name"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
                                error={projectNameError}
                            />
                        </div>

                        {/* Project Address */}
                        <div className="col-12">
                            <Textarea
                                key={`projectAddress`}
                                id="projectAddress"
                                label="Project Address"
                                labelHtmlFor="projectAddress"
                                required={true}
                                value={projectAddress}
                                placeholder="Project Address"
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProjectAddress(e.target.value)}
                                error={projectAddressError}
                            />
                        </div>

                        {/* Building Inspection Companies */}
                        <div className="col-12">
                            <div className="page_form_group mb-2">
                                <label
                                    htmlFor="buildingInspectionCompanies"
                                    className="custom_form_label is_required"
                                >
                                    Building Inspection Companies
                                </label>
                                <div className="input_with_button_area">
                                    <InputText
                                        key={`buildingInspectionCompanies`}
                                        id="buildingInspectionCompanies"
                                        value={selectedBuildingInspectionCompanies?.length > 0 ?
                                            selectedBuildingInspectionCompanies.map(company => company.company_name).join(', ') : ""}
                                        className="custom_form_input has_add_value"
                                        placeholder="Click to select company/companies."
                                        readOnly={true}
                                        autoComplete="off"
                                        onClick={handleShowBuildingInspectionCompanyModal}
                                    />
                                    <button
                                        className="add_value_btn p-ripple"
                                        onClick={handleShowBuildingInspectionCompanyModal}
                                    >
                                        {selectedBuildingInspectionCompanies?.length === 0 ? (
                                            <React.Fragment>
                                                <i className="bi bi-plus-lg"></i>
                                                <span>Select</span>
                                            </React.Fragment>
                                        ) : (
                                            <React.Fragment>
                                                <i className="bi bi-pencil-square"></i>
                                                <span>Change</span>
                                            </React.Fragment>
                                        )}
                                        <Ripple />
                                    </button>
                                </div>

                                {selectedBuildingInspectionCompaniesError && (
                                    <small className="form_error_msg">{selectedBuildingInspectionCompaniesError}</small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Building Inspection Companies Modal */}
            <Dialog
                visible={showBuildingInspectionCompanyModal}
                footer={buildingInspectionCompanyModalFooter}
                headerClassName="d-none"
                dismissableMask
                draggable={false}
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={() => handleCloseBuildingInspectionCompanyModal(true)}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <DataListModalSearch
                            key={`buildingInspectionCompanySearchKey`}
                            inputId="buildingInspectionCompanySearchKey"
                            value={buildingInspectionCompanySearchKey}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuildingInspectionCompanySearchKey(e.target.value)}
                            placeholder="Search company by name/email"
                            disabled={buildingInspectionCompanies?.length === 0}
                            onClear={() => setBuildingInspectionCompanySearchKey("")}
                        />

                        <DataListModalClose
                            onClose={() => handleCloseBuildingInspectionCompanyModal(true)}
                        />
                    </div>

                    {loadingBuildingInspectionCompanies ? (
                        <CustomTableLoader />
                    ) : errorBuildingInspectionCompanies ? (
                        <div className="data_select_modal_body custom">
                            <DataException
                                containerClassName="custom_height"
                                message={errorBuildingInspectionCompanies}
                            />
                        </div>
                    ) : (
                        <div className="data_select_modal_body custom">
                            {filteredBuildingInspectionCompanies && filteredBuildingInspectionCompanies?.length > 0 ? (
                                <div className="data_select_option_area">
                                    {filteredBuildingInspectionCompanies?.map((company) => (
                                        <div key={`buildingInspectionCompany_${company?.id}`} className="data_select_option_check_area">
                                            <Checkbox
                                                onChange={(e) => handleSelectBuildingInspectionCompany(company, e.checked || false)}
                                                checked={isSelectedBuildingInspectionCompany(company)}
                                            ></Checkbox>

                                            <div
                                                className={`data_select_option p-ripple 
                                                        ${isSelectedBuildingInspectionCompany(company) ? "selected" : ""}
                                                    `}
                                                onClick={() => handleSelectBuildingInspectionCompany(company, !isSelectedBuildingInspectionCompany(company))}
                                            >
                                                <span>
                                                    {company?.company_name}
                                                    <span className="text_subtext lower">&nbsp;|&nbsp;{company?.email ?? ''}</span>
                                                </span>
                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <DataException
                                    containerClassName="custom_height"
                                    message={buildingInspectionCompanySearchKey ? 'The company you were looking for is not found!' : `No companies found.`}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Dialog>


            {/* Building Control Quotation Response Modal */}
            <Dialog
                visible={showResponseQuotationModal}
                headerClassName="custom_modal_header"
                header={responseQuotationModalHeader}
                footer={responseQuotationModalFooter}
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseResponseQuotationModal}
                dismissableMask={false}
                draggable={false}
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        {(isArchitect || isClient) && (
                            <>
                                {(() => {
                                    const isApproved = selectedBuildingControlQuotation?.status === 'Finished' && selectedBuildingControlQuotationCompany?.is_approved === 1;
                                    const isRejected = selectedBuildingControlQuotation?.status === 'Finished' && selectedBuildingControlQuotationCompany?.is_approved === 0;
                                    const isPending = selectedBuildingControlQuotation?.status === 'Pending' && selectedBuildingControlQuotationCompany?.respone_data !== null;
                                    const isNotResponded = selectedBuildingControlQuotation?.status === 'Pending' && selectedBuildingControlQuotationCompany?.respone_data === null;

                                    let message = "";

                                    if (isApproved) {
                                        message = `This company's quotation has been approved.`
                                    } else if (isRejected) {
                                        message = `This company's quotation has been not approved.`
                                    } else if (isPending) {
                                        message = `This company's quotation is still pending.`
                                    } else if (isNotResponded) {
                                        message = `This company's quotation has not been responded yet.`
                                    } else {
                                        message = ""
                                    }

                                    return (
                                        <div className="col-12">
                                            <Message
                                                severity={isApproved ? 'success' : isRejected ? 'error' : isPending ? 'info' : 'warn'}
                                                className="message_text w-100 mb-4"
                                                icon={isApproved ? "bi bi-check-circle" : isRejected ? "bi bi-x-circle" : isPending ? "bi bi-clock" : "bi bi-exclamation-circle"}
                                                style={{ border: '1px solid', borderRadius: '8px' }}
                                                text={message}
                                            />
                                        </div>
                                    )
                                })()}
                            </>
                        )}

                        {(isArchitect || isClient) ? (
                            <>
                                {/* Project details */}
                                <div className="col-12 mb-3">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-buildings me-2"></i>
                                        Project details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Project"
                                            value={`${selectedBuildingControlQuotation?.project_detail?.job_number} : ${selectedBuildingControlQuotation?.project_detail?.full_address}`}
                                        />
                                    </article>
                                </div>

                                {/* Inspection company details */}
                                <div className="col-12 mb-3">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-person-vcard me-2"></i>
                                        Inspection company details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Company name"
                                            value={selectedBuildingControlQuotationCompany?.company?.company_name}
                                        />
                                        <ViewDataGroup
                                            title="Email"
                                            value={selectedBuildingControlQuotationCompany?.company?.email}
                                        />
                                        <ViewDataGroup
                                            title="Address"
                                            value={selectedBuildingControlQuotationCompany?.company?.address}
                                        />
                                    </article>
                                </div>
                            </>
                        ) : isInspector ? (
                            <>
                                {/* Project details */}
                                <div className="col-12 mb-3">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-buildings me-2"></i>
                                        Project details
                                    </h4>

                                    <article className="data_view_area">
                                        <ViewDataGroup
                                            title="Project"
                                            value={`${selectedBuildingControlQuotationRequest?.project_detail?.job_number} : ${selectedBuildingControlQuotationRequest?.project_detail?.full_address}`}
                                        />
                                    </article>
                                </div>
                            </>
                        ) : null}

                        {(isArchitect && selectedBuildingControlQuotation?.status === 'Pending' && (selectedBuildingControlQuotationCompany?.respone_data === null || selectedBuildingControlQuotationCompany?.respone_data?.responce_added_arc === 1)) ||
                            (isInspector && selectedBuildingControlQuotationRequest?.status === 'Pending' && (selectedBuildingControlQuotationRequest?.responce_data === null || selectedBuildingControlQuotationRequest?.responce_data?.responce_added_arc === 0)) ? (
                            <>
                                <div className="col-12">
                                    <h4 className="view_data_head">
                                        <i className="bi bi-chat-left-dots me-2"></i>
                                        Response
                                    </h4>

                                    {/* Response Fields */}
                                    {(() => {
                                        const activeFields = getResponseFields();

                                        return activeFields.length > 0 ? (
                                            <article className="data_view_area mb-3">
                                                {activeFields.map((responseField) => {
                                                    const fieldValue = responseFieldValues.get(responseField.id);
                                                    const fieldError = responseFieldErrors.get(responseField.id) ?? '';

                                                    return (
                                                        <div className="view_data_group" id={`response_field_${responseField.id}`} key={`response_field_${responseField.id}`}>
                                                            <div className="row">
                                                                <div className="col-6 d-flex align-items-center">
                                                                    <h5 className="view_data_title">
                                                                        {responseField.order_number}: {responseField.title}{responseField.is_requireded === 1 && <span className="text-danger ms-1">*</span>} :
                                                                    </h5>
                                                                </div>

                                                                <div className="col-6">
                                                                    <h5 className="view_data_content">
                                                                        {responseField.answer_type === 'string' ? (
                                                                            <TextInput
                                                                                id={`bcq_response_field_${responseField.id}`}
                                                                                required={responseField.is_requireded === 1}
                                                                                value={typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '')}
                                                                                placeholder={responseField.title ?? ''}
                                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                                    handleResponseFieldChange(responseField.id, e.target.value)
                                                                                }
                                                                                formGroupClassName="mb-0 w-100"
                                                                                error={fieldError}
                                                                            />
                                                                        ) : (
                                                                            <NumberInput
                                                                                inputId={`bcq_response_field_${responseField.id}`}
                                                                                placeholder={responseField.title ?? ''}
                                                                                onChange={(e: InputNumberChangeEvent) =>
                                                                                    handleResponseFieldChange(responseField.id, e.value ?? null)
                                                                                }
                                                                                value={typeof fieldValue === 'number' ? fieldValue : (fieldValue !== null && fieldValue !== undefined && fieldValue !== '' ? Number(fieldValue) : null)}
                                                                                required={responseField.is_requireded === 1}
                                                                                formGroupClassName="mb-0 w-100"
                                                                                min={0}
                                                                                error={fieldError}
                                                                            />
                                                                        )}
                                                                    </h5>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </article>
                                        ) : (
                                            <DataException
                                                containerClassName="custom_height mb-4"
                                                message="Response data not available"
                                            />
                                        );
                                    })()}
                                </div>

                                {/* Response Files */}
                                <div className="col-12">
                                    <div className="page_form_group mb-0">
                                        <label htmlFor="" className="custom_form_label">Files</label>

                                        <div className="checklist_img_container mt-2 mb-1">
                                            {(() => {
                                                const responseFiles = getResponseFiles();

                                                return responseFiles.length > 0 && (
                                                    <React.Fragment>
                                                        {responseFiles?.map((file, index) => (
                                                            <div className="note_image_container response_image_container" key={file?.id}>
                                                                {responseFileDeleteLoadingId === file?.id ? (
                                                                    <FileActionLoader />
                                                                ) : (
                                                                    <React.Fragment>
                                                                        {file?.extension === "pdf" ? (
                                                                            <div
                                                                                className="response_doc_file_container"
                                                                                onClick={() => handleViewDocument(file?.file_path!)}
                                                                            >
                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                            </div>
                                                                        ) : (
                                                                            <ImagePreview
                                                                                src={file?.file_path!}
                                                                                alt={`arc_response_img_${index + 1}`}
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleDeleteResponseFile(file?.id!)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })()}

                                            {responseFiles && responseFiles?.length > 0 && (
                                                <React.Fragment>
                                                    {responseFiles.map((file, index) => {
                                                        const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                        return (
                                                            <div
                                                                className="note_image_container response_image_container"
                                                                key={index}
                                                            >
                                                                {fileExtension === "pdf" ? (
                                                                    <div
                                                                        className="response_doc_file_container"
                                                                        onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                    </div>
                                                                ) : (
                                                                    <ImagePreview
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`arc_response_img_${index + 1}`}
                                                                    />
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleRemoveResponseFile(index)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            )}

                                            <Tooltip target={`.img_upload_label`} />

                                            <label
                                                htmlFor="responseFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="responseFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleUploadResponseFiles}
                                                />
                                                <Ripple />
                                            </label>
                                        </div>

                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>

                                        {Object.values(responseFilesError).length > 0 && (
                                            <>
                                                {Object.values(responseFilesError).map((errMsg, i) => (
                                                    <small key={i} className="form_error_msg d-block">{errMsg}</small>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="col-12">
                                <h4 className="view_data_head">
                                    <i className="bi bi-chat-left-dots me-2"></i>
                                    Response
                                </h4>

                                {/* Response data */}
                                {(() => {
                                    const bcQuotationResponse = getResponseFields();

                                    return bcQuotationResponse.length > 0 ? (
                                        <article className="data_view_area">
                                            {bcQuotationResponse?.map((response) => (
                                                <ViewDataGroup
                                                    key={`response_data_${response.id}`}
                                                    title={response.title}
                                                    value={response.responce?.reply}
                                                />
                                            ))}
                                        </article>
                                    ) : (
                                        <div className="col-12">
                                            <DataException
                                                containerClassName="custom_height"
                                                message="Response data not available"
                                            />
                                        </div>
                                    );
                                })()}

                                {/* Response files */}
                                {(() => {
                                    const bcQuotationResponseFiles = getResponseFiles();

                                    return bcQuotationResponseFiles.length > 0 && (
                                        <div className="page_form_group mt-3 mb-0">
                                            <label className="custom_form_label">Files</label>

                                            <div className="checklist_img_container mt-2 mb-1">
                                                {bcQuotationResponseFiles?.map((file) => (
                                                    <div className="note_image_container response_image_container" key={`bcQuotationResponseFiles_${file?.id}`}>
                                                        {file?.extension === "pdf" ? (
                                                            <div
                                                                className="response_doc_file_container"
                                                                onClick={() => handleViewDocument(file?.file_path!)}
                                                            >
                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                            </div>
                                                        ) : (
                                                            <ImagePreview
                                                                src={file?.file_path!}
                                                                alt={`bcQuotationResponseFiles_${file?.id}`}
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                            </div>
                        )}
                    </div>
                </div>
            </Dialog>


            {/* Quotation Compare Companies Modal */}
            <Dialog
                visible={showCompareCompaniesModal}
                header={compareCompaniesModalHeader}
                footer={compareCompaniesModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                dismissableMask={false}
                draggable={false}
                onHide={handleCloseCompareCompaniesModal}
            >
                <div className="custom_modal_body data_select_modal_area pt-2">
                    {selectedComparisonCompanies && selectedComparisonCompanies?.length > 0 && selectedComparisonCompanies?.length < 2 && (
                        <div className="p-3 pb-2">
                            <Message
                                severity='warn'
                                className="message_text w-100"
                                icon="bi bi-exclamation-circle"
                                style={{ border: '1px solid', borderRadius: '8px' }}
                                text={`Please select at least two companies to compare quotation.`}
                            />
                        </div>
                    )}

                    <div className="data_select_modal_body custom">
                        {(
                            selectedBuildingControlQuotation &&
                            selectedBuildingControlQuotation?.quotation_share_list &&
                            selectedBuildingControlQuotation?.quotation_share_list?.length > 0
                        ) ? (
                            <div className="data_select_option_area">
                                {selectedBuildingControlQuotation?.quotation_share_list?.map((quotationShare) => {
                                    const notResponded = quotationShare?.respone_data === null;

                                    return (
                                        <div key={`compareCompany_${quotationShare?.id}`} className="data_select_option_check_area">
                                            <Checkbox
                                                onChange={(e) => handleSelectQuotationShareListCompany(quotationShare, e.checked || false)}
                                                checked={isSelectedQuotationShareCompany(quotationShare)}
                                                disabled={notResponded}
                                            ></Checkbox>

                                            <div
                                                className={`data_select_option ${notResponded ? 'disabled' : ''} p-ripple 
                                                        ${isSelectedQuotationShareCompany(quotationShare) ? "selected" : ""}
                                                    `}
                                                onClick={() => {
                                                    if (notResponded) return;
                                                    handleSelectQuotationShareListCompany(quotationShare, !isSelectedQuotationShareCompany(quotationShare));
                                                }}
                                            >
                                                <span>
                                                    {quotationShare?.company?.company_name ?? '---'}
                                                    <span className="text_subtext lower">&nbsp;|&nbsp;{quotationShare?.company?.email ?? '---'}</span>

                                                    {notResponded && (
                                                        <small className="text-danger">&nbsp;(Not yet responded)</small>
                                                    )}
                                                </span>
                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <DataException
                                containerClassName="custom_height"
                                message={`The quotation has not been shared with any companies yet.`}
                            />
                        )}
                    </div>
                </div>
            </Dialog>


            {/* Quotation Comparison Modal */}
            <Dialog
                visible={showCompareQuotationModal}
                headerClassName="custom_modal_header"
                header={compareQuotationModalHeader}
                className="custom_modal_dialog modal_dialog_lg"
                onHide={handleCloseCompareQuotationModal}
                dismissableMask={false}
                closeOnEscape={false}
                draggable={false}
                closable={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <h4 className="view_data_head">
                        <i className="bi bi-card-list me-2"></i>
                        Quotation details
                    </h4>

                    <article className="data_view_area">
                        <ViewDataGroup
                            title="Project"
                            titleWrapClassName="col-5 col-sm-4"
                            valueWrapClassName="col-7 col-sm-8"
                            value={`${selectedBuildingControlQuotation?.project_detail?.job_number} : ${selectedBuildingControlQuotation?.project_detail?.full_address}`}
                        />
                    </article>

                    {quotationComparisonData?.length === 0 ? (
                        <DataException
                            containerClassName="custom_height"
                            message="No comparison data available."
                        />
                    ) : (
                        <div className="mt-4">
                            <DataTable
                                key={`quotationComparison`}
                                value={[...quotationComparisonData].sort((a, b) => Number(a.order_number ?? 0) - Number(b.order_number ?? 0))}
                                size="small"
                                dataKey="id"
                                className="page_table comparison_table p-0"
                                rowClassName={() => 'project_data_row secondary'}
                                scrollable
                                tableStyle={{ minWidth: `${Math.max(50, 20 + selectedComparisonCompanies.length * 20)}rem` }}
                                emptyMessage="No quotation comparison data found!"
                            >
                                <Column
                                    header="Field"
                                    headerClassName="text_purple"
                                    field="title"
                                    body={(rowData: BuildingControlQuotationComparison) => (
                                        <span className="comparision_table_field fw-semibold text-capitalize">
                                            {String(rowData.order_number).padStart(2, '0')}: {rowData.title ?? '---'}
                                        </span>
                                    )}
                                    style={{ minWidth: '180px', width: '22%' }}
                                    frozen
                                />

                                {selectedComparisonCompanies.map((companyShare) => (
                                    <Column
                                        key={`cmp_col_${companyShare.company_id}`}
                                        header={
                                            <div className="d-flex flex-column justify-content-center align-items-center gap-1">
                                                <span className="fw-semibold text-capitalize">
                                                    {companyShare.company?.company_name ?? `Company ${companyShare.company_id}`} <span className="text_subtext lower"> | {companyShare.company?.email ?? ''}</span>
                                                </span>

                                                {isClient && (
                                                    <Button
                                                        label="Approve"
                                                        icon="bi bi-check-circle"
                                                        size="small"
                                                        className="table_status_btn"
                                                        severity="success"
                                                        style={{ fontSize: '0.85rem', padding: '4px 10px', width: '100px' }}
                                                        onClick={() => handleApproveQuotation(companyShare.company_id)}
                                                    />
                                                )}
                                            </div>
                                        }
                                        headerClassName="text_purple"
                                        alignHeader="center"
                                        style={{ minWidth: '160px' }}
                                        body={(rowData: BuildingControlQuotationComparison) => {
                                            const match = rowData.comparison_data?.find(
                                                (d) => d.company_id === companyShare.company_id
                                            );

                                            const replyValue = match?.reply;
                                            const isHighlight = match?.is_highlight ?? false;
                                            const isMin = match?.is_min ?? false;

                                            return (
                                                <div className={`comparision_table_field d-flex align-items-center justify-content-center gap-2 ${isHighlight ? 'text-success text_bold is_highlight' : ''}`}>
                                                    <span>
                                                        {replyValue !== null && replyValue !== undefined && replyValue !== ''
                                                            ? String(replyValue)
                                                            : '---'}
                                                    </span>

                                                    {isMin && replyValue !== null && replyValue !== undefined && replyValue !== '' && (
                                                        <Badge value="Lowest" severity="success"></Badge>
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />
                                ))}
                            </DataTable>
                        </div>
                    )}
                </div>
            </Dialog>


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showItemDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}
        </React.Fragment >
    );
};

export default BuildingControlQuotation;